import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLEndorsementFlowUtil, MLCapitalizeStringFilter, MLEndorsementUtil } from 'ml-portals-util-js';
import { MLDriverVehicleUtil, MLNotificationBlockUtil, MLAvailableInsuredCandidatesUtil, MLExceptionErrorHandler } from 'ml-portals-util-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { MLPolicyChangeDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { MLAddVehicleOperatorComponent, MLVehicleOperatorStatusComponent, MLDriverChangeDisclosureComponent } from 'ml-capability-policychange-common-react';
import { MLInsuredCandidatesComponent } from 'ml-components-common-react';
import metadata from './VehicleOperatorPagePolChange.metadata.json5';
import messages from './VehicleOperatorPagePolChange.messages';
import styles from './VehicleOperatorPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const alertNames = {
    removedOperatorName: 'removedOperator',
    addedOperatorName: 'addedOperator',
    notValidReason: 'notValidReason',
    notValidReasonToRemoveExcludedMember: 'notValidReasonToRemoveExcludedMember',
    driverHasBeenAddedAsNonDriver: 'driverHasBeenAddedAsNonDriver',
    driverExcludedNotCovered: 'driverExcludedNotCovered',
    exclusionNotAllowed: 'exclusionNotAllowed',
    removeOperator: 'removeOperator',
    vehicleOperatorErrorMessage: 'vehicleOperatorErrorMessage',
    vehicleOperatorSaveErrorMessage: 'vehicleOperatorSaveErrorMessage'
};

const VehicleOperatorPagePolChange = (props) => {
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        changeNextSteps,
        currentStep
    } = props;
    const { submissionVM, alertMessages } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const { handleExceptionErrors , clearExceptionErrors } = MLExceptionErrorHandler();
    const {
        showProgressDialog
    } = MLModalService();

    const {
        clearBlockAlerts,
        addErrorAlert,
        addWarningAlert
    } = MLNotificationBlockUtil();

    const {
        isSamePerson,
        validateExcludedDriverAddition,
        checkUniqueSelf,
        checkUniqueSpouse
    } = MLDriverVehicleUtil();

    const {
        addRemovedVehicleOperatorToAvailableCandidates
    } = MLAvailableInsuredCandidatesUtil();

    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const VEHICLE_OPERATORS_PATH = 'lobData.personalUmbrella.coverables.vehicleOperators';
    const vehicleOperatorsVM = _.get(submissionVM, VEHICLE_OPERATORS_PATH, []);
    const canExcludeDrivers = _.get(submissionVM, 'baseData.canExcludeDrivers_ML.value');
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isPersonalUmbrella = submissionVM.value.isPersonalUmbrella();
    const isUMB_NP = isModernProduct && isPersonalUmbrella;
    const NON_DRIVER_TYPES = ['nondriver', 'permitted', 'outofhousehold']; 

    const [isAddOrEditOrRemove, setAddOrEditOrRemove] = useState(undefined);
    const [initOperator, setInitOperator] = useState({ initOperatorsType: '', initOperatorsStatus: '' });
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const [addHouseMemberWarning, setAddHouseMemberWarning] = useState(false);
    const [nonEditingOperators, setNonEditingOperators] = useState([]);
    const [insuredCandidateSelected, setInsuredCandidateSelected] = useState(false);
    const [operatorType, setOperatorType] = useState(currentStep.driverType || MLPolicyChangeDataHolderService.getDriver().driverType);
    const [isBackwardsNavigation, setIsBackwardsNavigation] = useState(false);
    // US985436 -> new state variable to determine if child component has error
    const [isComponentErrorPresent, setIsComponentErrorPresent] = useState(false);

    const { onValidate } = useValidation('VehicleOperatorPagePolChange');

    const initSelectedVehicleOperator = () => {
        let index = -1;
        if (!currentStep.existingStep) {
            const driver = MLPolicyChangeDataHolderService.getDriver();
            index = vehicleOperatorsVM.value.findIndex((vo) => vo.fixedID === driver.fixedID || !vo.fixedID);
        }
        if (index === -1) {
            index = vehicleOperatorsVM.value.findIndex((vo) => vo.fixedID === currentStep.fixedId);
        }
        if (index === -1) index = 0;
        return vehicleOperatorsVM.getElement(index);
    };

    const selectedVehicleOperator = initSelectedVehicleOperator();

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const getPageAction = (operator) => {
        let pageAction = 'add';
        if (operator.publicID.value && MLPolicyChangeDataHolderService.getPageAction() !== 'add') {
            pageAction = operator.value.isToRemove ? 'remove' : 'edit';
        }
        return pageAction;
    };

    const setFixIdsOfNonEditingOperators = () => {
        const operatorPublicIDs = [];
        vehicleOperatorsVM.value.forEach((vo) => {
            if (vo.publicID && !vo.isSelected) {
                operatorPublicIDs.push(vo.publicID);
            }
        });
        setNonEditingOperators(operatorPublicIDs);
    };

    const setScopeVariables = () => {
        const pageAction = MLPolicyChangeDataHolderService.getPageAction();
        selectedVehicleOperator.value.isSelected = true;
        selectedVehicleOperator.value.isDriverStatusForRemovingRequired = (pageAction === 'remove');

        setAddOrEditOrRemove(pageAction);
        setInitOperator({
            initOperatorsType: selectedVehicleOperator.value.driverType,
            initOperatorsStatus: selectedVehicleOperator.value.driverStatusForAdding
        });
    };

    const initVehicleOperatorPage = () => {
        if (currentStep.existingStep || selectedVehicleOperator.fixedID.value) {
            setIsBackwardsNavigation(true);
            setInsuredCandidateSelected(true);
        }
        setFixIdsOfNonEditingOperators();
        if (currentStep.pageAction) {
            MLPolicyChangeDataHolderService.setPageAction(currentStep.pageAction);
        }
        if (currentStep.driverType) {
            MLPolicyChangeDataHolderService.setDriverType(currentStep.driverType);
            setOperatorType(currentStep.driverType);
        }
        setScopeVariables();
        if (alertMessages.length > 0) {
            const poppedAlert = alertMessages.pop();
            clearBlockAlerts(poppedAlert.alertName);
        }
    };

    const getDriverTypeDisplayName = (isSingular) => {
        switch (operatorType) {
            case 'rated':
                return isSingular ? translator(messages.driver) : translator(messages.drivers);
            case 'excluded':
                return isSingular ? translator(messages.excludedDriver) : translator(messages.excludedDrivers);
            case 'household':
                if(isUMB_NP) {
                    return isSingular ? translator(messages.nonDriver) : translator(messages.nonDrivers);
                }
                return isSingular ? translator(messages.householdMember) : translator(messages.householdMembers);
            case 'nondriver':
                return isSingular ? translator(messages.nonDriver) : translator(messages.nonDrivers);
            case 'excluded':
                return isSingular ? translator(messages.excludedMember) : translator(messages.excludedMembers);
            default:
                return '';
        }
    };

    const getFullNameFormatted = (name) => {
        let suffixDisplayName = '';
        if (name.suffix) {
            const nameSuffixList = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
            const suffixTypeKeyName = nameSuffixList.getCode(name.suffix).name;
            suffixDisplayName = translator(
                {
                    id: suffixTypeKeyName,
                    defaultMessage: ' '
                }
            );
        }
        let fullName;
        if (selectedVehicleOperator.driverType.value.code === 'household') {
            fullName = MLCapitalizeStringFilter.capitalize(name.getFullNameWithoutSuffix());
        } else {
            fullName = `${MLCapitalizeStringFilter.capitalize(name.getFullNameWithoutSuffix())} ${suffixDisplayName}`;
        }
        return fullName;
    };

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        initVehicleOperatorPage();

        if (MLPolicyChangeDataHolderService.getPageAction() === 'edit') {
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.editX, { driverType: getDriverTypeDisplayName(true) }),
                !currentStep.existingStep, currentStep.mlStepName, authHeader);
        } else if (MLPolicyChangeDataHolderService.getPageAction() === 'add') {
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.addX, { driverType: getDriverTypeDisplayName(true) }),
                !currentStep.existingStep, currentStep.mlStepName, authHeader);
        } else if (selectedVehicleOperator.value.isToRemove) {
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.removeX, { driverName: getDriverTypeDisplayName(true) }),
                !currentStep.existingStep, currentStep.mlStepName, authHeader);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        const addVehicleOperatorSavingAlert = () => {
            addWarningAlert(messages.saveAddHouseholdMemberWarning, alertNames.vehicleOperatorSaveErrorMessage);
            window.scrollTo(0, 0);
        };

        if (addHouseMemberWarning)
            addVehicleOperatorSavingAlert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addHouseMemberWarning]);


    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const getRemoveLabel = () => {
        if (operatorType === 'household') {
            return translator(messages.removeHouseholdMember);
        }
        if (operatorType === 'excluded') {
            return translator(messages.removeExcludedDriver);
        }
        if (operatorType === 'nondriver') {
            return translator(messages.removeNonDriver);
        }
        return translator(messages.removeX, { driverName: getFullNameFormatted(selectedVehicleOperator.contact.value) });
    };

    const addRatedDriverAlert = (item) => {
        const alertMsg = {
            alertName: alertNames.addedOperatorName,
            formattedMessageName: 'driverHasBeenAdded',
            messagePram: getFullNameFormatted(item.contact)
        };
        alertMessages.push(alertMsg);
    };

    const addNonDriverAlert = (item) => {
        const alertMsg = {
            alertName: alertNames.driverHasBeenAddedAsNonDriver,
            formattedMessageName: 'driverHasBeenAddedAsNonDriver',
            messagePram: getFullNameFormatted(item.contact)
        };
        alertMessages.push(alertMsg);
    };

    const notValidreasonAlert = () => {
        const alertMsg = {
            alertName: alertNames.notValidReason,
            formattedMessageName: 'notValidReason',
        };
        alertMessages.push(alertMsg);
    };

    const notValidreasonAlertForExcluded = () => {
        const alertMsg = {
            alertName: alertNames.notValidReasonToRemoveExcludedMember,
            formattedMessageName: 'notValidReasonToRemoveExcludedMember',
        };
        alertMessages.push(alertMsg);
    };

    const addHouseholdAlert = (item) => {
        const alertMsg = {
            alertName: alertNames.removeOperator,
            formattedMessageName: 'driverHasBeenAddedAsHouseholdMember',
            messagePram: getFullNameFormatted(item.contact)
        };
        alertMessages.push(alertMsg);
    };

    const undoOperatorExclusion = (item) => {
        const operatorToUndo = vehicleOperatorsVM.value.find((vo) => !nonEditingOperators.includes(vo.publicID));
        operatorToUndo.driverType = initOperator.initOperatorsType;
        const initStatus = isAddOrEditOrRemove === 'add' ? item.driverStatusForAdding : initOperator.initOperatorsStatus;
        operatorToUndo.driverStatusForAdding = initStatus;
        operatorToUndo.driverStatusForRemoving = initStatus;
        /*
        DE254372 & DE254373
        In a policy change when we add a driver and click undo, the added driver should be removed from the policy
        For all other cases, i.e making a rater driver excluded or household excluded etc the driver should be added back to the original list
        */
        if(isAddOrEditOrRemove === 'add') {
            const removeDriverIndex = vehicleOperatorsVM.value.findIndex((vo) => vo.publicID === operatorToUndo.publicID);
            vehicleOperatorsVM.value.splice(removeDriverIndex, 1);
        }
        const poppedAlert = alertMessages.pop();
        clearBlockAlerts(poppedAlert.alertName);
    };

    const addExcludedAlert = (item) => {
        const help = {
            title: translator(messages.driverMustBeRatedOrExcluded),
            message: translator(messages.driverExclusionHelpText)
        };

        const alertMsg = {
            alertName: alertNames.removedOperatorName,
            formattedMessageName: 'driverExcludedNotCovered',
            messagePram: getFullNameFormatted(item.contact),
            actionFn: () => undoOperatorExclusion(item),
            help: help
        };
        alertMessages.push(alertMsg);
    };

    const undoOperatorRemoval = (item) => {
        item.isSelected = false;
        vehicleOperatorsVM.pushElement(item);
        const poppedAlert = alertMessages.pop();
        clearBlockAlerts(poppedAlert.alertName);
    };

    const addRemovalAlert = (item) => {
        const displayName = item.driverType === 'household' ? item.contact.fullName : item.contact.displayName;
        alertMessages.push({
            alertName: alertNames.removedOperatorName,
            formattedMessageName: 'driverHasBeenRemoved',
            messagePram: displayName,
            actionFn: () => undoOperatorRemoval(item)
        });
    };

    const addVehicleOperatorErrorAlert = (errorMsg) => {
        const secondNamedInsuredErrorMsg = translator(messages.notAValidReasonToExcludeSecondaryNamedInsured);
        const secondNamedInsuredErrorMsg2 = translator(messages.notAValidReasonToRemoveSecondaryInsured);
        if (isAMP && (errorMsg === secondNamedInsuredErrorMsg || errorMsg === secondNamedInsuredErrorMsg2)) {
            const help = {
                title: translator(messages.whatIsSecondaryNamedInsured),
                message: translator(messages.definitionOfSecondaryNamedInsured)
            };
            addErrorAlert(errorMsg, alertNames.vehicleOperatorErrorMessage, help.title, help.message);
        } else {
            addErrorAlert(errorMsg, alertNames.vehicleOperatorErrorMessage);
        }
        window.scrollTo(0, 0);
    };

    const addExclusionNotAllowedAlert = (memberType) => {
        let msg;
        if (_.includes(['rated', 'household'], memberType.code)) {
            msg = translator(messages.notAValidReasonToRemoveThisDriver);
        }
        const alertMsg = {
            type: 'warning',
            alertName: alertNames.exclusionNotAllowed,
            message: msg
        };
        alertMessages.push(alertMsg);
    };

    const isSecondaryNamedInsured = (operator) => {
        if (submissionVM.baseData.secondaryNamedInsured_ML) {
            return isSamePerson(operator.contact.value, submissionVM.baseData.secondaryNamedInsured_ML.value);
        }
    };

    const hasMultipleSpouses = () => {
        const spouseOperators = vehicleOperatorsVM.children.filter((vo) => vo.relationshipToInsured.value.code === 'spouse');
        return spouseOperators.length > 1;
    };

    const isStateND = submissionVM.baseData.policyAddress.state.value.code === 'ND';
    const isStateMD = submissionVM.baseData.policyAddress.state.value.code === 'MD';
    const isStateCO = submissionVM.baseData.policyAddress.state.value.code === 'CO';

    const isExcludedReason = (operatorCode) => {
        return ['license_revoked', 'unwanted'].indexOf(operatorCode) > -1;
    };

    const isHouseholdReason = (operatorCode) => {
        return ['own_insurance', 'never_licensed', 'abroad' , 'mission' ,'permit'].indexOf(operatorCode) > -1;
    };

    const isRemoveReason = (operatorCode) => {
        return ['not_household', 'duplicate_driver_ml' , 'deceased' , 'unknown', 'notresiding'].indexOf(operatorCode) > -1;
    };

    const vehicleOperatorEditRules = (operator, removedObj) => {
        const error = { errorMsg: null, errorHelpTitle: null, errorHelpMessageContent: null };
        if (operator.relationshipToInsured.value.code === 'spouse' && hasMultipleSpouses()) {
            error.errorMsg = translator(messages.spouseWasAlreadySelected);
        } else if (operator.relationshipToInsured.value.code === 'self' && !checkUniqueSelf(vehicleOperatorsVM.value)) {
            error.errorMsg = translator(messages.selfWasAlreadySelected);
        } else if (operator.driverStatusForAdding.value.code === 'not_household') {
            removedObj.removedOperator = operator.value;
            // save and set old status in case of undo hit on additional changes page
            const index = vehicleOperatorsVM.value.findIndex((vo) => vo === operator.value);
            addRemovedVehicleOperatorToAvailableCandidates(submissionVM, operator.value);
            vehicleOperatorsVM.value.splice(index, 1);
            if (isAddOrEditOrRemove === 'add' || (initOperator.initOperatorsStatus !== operator.value.driverStatusForAdding)) {
                addRemovalAlert(removedObj.removedOperator);
            }
        } else if (!isStateND && !isStateMD && isExcludedReason(operator.driverStatusForAdding.value.code) && (operator.relationshipToInsured.value.code === 'spouse')) {    
            error.errorMsg = isGPA ? translator(messages.spouseCanNotBeExcluded) : translator(messages.spouseCanNotBeRemoved);
        } else if (isExcludedReason(operator.driverStatusForAdding.value.code)) {
            if (canExcludeDrivers) {
                operator.driverType.value = 'excluded';
                if (isAddOrEditOrRemove === 'add' || (initOperator.initOperatorsStatus !== operator.value.driverStatusForAdding)) {
                    addExcludedAlert(operator.value);
                }
            } else {
                addExclusionNotAllowedAlert(operator.driverType.value);
                operator.driverType.value = 'rated';
            }
        } else if (!isUMB_NP && isHouseholdReason(operator.driverStatusForAdding.value.code)) {
            operator.driverType.value = 'household';
            if (isAddOrEditOrRemove === 'add' || (initOperator.initOperatorsStatus !== operator.value.driverStatusForAdding)) {
                addHouseholdAlert(operator.value);
            }
        } else if (operator.driverStatusForAdding.value.code === 'insure_driver' || operator.driverStatusForAdding.value.code === 'discovered') {
            operator.driverType.value = 'rated';
            if (isAddOrEditOrRemove === 'add' || (initOperator.initOperatorsStatus !== operator.value.driverStatusForAdding)) {
                addRatedDriverAlert(operator.value);
            }
        }
        if(isUMB_NP) {
            const newDriverType = driverTypeForNPUmb(operator);
            if (isAddOrEditOrRemove === 'add') {
               if(NON_DRIVER_TYPES.includes(newDriverType.driverType.value.code)) {
                 addNonDriverAlert(operator.value);
               }
            }
        }
        return error;
    };

    const getDriverCategory = (driverType) => {     
        if (NON_DRIVER_TYPES.includes(driverType)) {        
             return 'nondriver';     
        }     
        return driverType; 
    };
 

    const vehicleOperatorRemovalRules = (operator, removedObj) => {
        const error = { errorMsg: null, errorHelpTitle: null, errorHelpMessageContent: null };
        const relationshipCode = _.get(operator, 'relationshipToInsured.value.code');
        if(isUMB_NP) {
            const oldDriverType = operator?.driverType?.value?.code ;
            const newDriverType = driverRemovalTypeForNPUmb(operator);

            const oldDriverCategory = getDriverCategory(oldDriverType);
            const newDriverCategory = getDriverCategory(newDriverType);

            if(newDriverType && oldDriverCategory === newDriverCategory) {
                if(newDriverType === 'excluded') {
                    notValidreasonAlertForExcluded();
                } else{
                    notValidreasonAlert();
                }
            }
            if(  ['nondriver' , 'permitted' , 'outofhousehold'].includes(newDriverType) && !['nondriver' , 'permitted' , 'outofhousehold','household' ].includes(oldDriverType)) {
                addNonDriverAlert(operator.value);
            }
            if(newDriverType === 'excluded' && oldDriverType !== 'excluded') {
                addExcludedAlert(operator.value);
            }
            if(newDriverType) {
                return error;
            }
        }
        if (operator.driverStatusForAdding && operator.driverStatusForAdding.value !== operator.driverStatusForRemoving.value) {
            operator.driverStatusForAdding.value = operator.driverStatusForRemoving.value;
        }

        if (!canExcludeDrivers && isExcludedReason(operator.driverStatusForRemoving.value.code)) {
            addExclusionNotAllowedAlert(operator.driverType.value);
            operator.driverType.value = 'rated';
        } else if (operator.isPrimaryNamedInsured.value && ((!isStateND && operator.driverStatusForRemoving.value.code)) || (isStateND && (operator.driverStatusForRemoving.value.code !== 'unwanted' && operator.driverStatusForRemoving.value.code !== 'license_revoked'))) {
            error.errorMsg = translator(messages.notAValidReasonToRemovePrimaryNamedInsured);
            window.scrollTo(0, 0);
        } else if ((isRemoveReason(operator.driverStatusForRemoving.value.code)) && relationshipCode !== 'spouse') {
            removedObj.removedOperator = operator.value;
        } else if (isGPA && isStateMD && isSecondaryNamedInsured(operator) && operator.value.driverStatusForRemoving === 'unwanted') {
            error.errorMsg = translator(messages.notAValidReasonToRemoveExclusion)
        } else if (!isStateND && !isStateMD && isExcludedReason(operator.driverStatusForAdding.value.code) && (operator.relationshipToInsured.value.code === 'spouse')) {    
            error.errorMsg = isGPA ? translator(messages.spouseCanNotBeExcluded) : translator(messages.spouseCanNotBeRemoved);
        } else if (isExcludedReason(operator.driverStatusForAdding.value.code)) {
            if (canExcludeDrivers) {
                operator.driverType.value = 'excluded';
                if (isAddOrEditOrRemove === 'add' || (initOperator.initOperatorsStatus !== operator.value.driverStatusForAdding)) {
                    addExcludedAlert(operator.value);
                }
            } else {
                addExclusionNotAllowedAlert(operator.driverType.value);
                operator.driverType.value = 'rated';
            }
        } else if ((!isStateND && !isStateMD) && isRemoveReason(operator.driverStatusForRemoving.value.code) && relationshipCode === 'spouse') {
            error.errorMsg = isGPA ? translator(messages.spouseCanNotBeExcluded) : translator(messages.spouseCanNotBeRemoved);
            window.scrollTo(0, 0);
        } else if ((!isStateND && !isStateMD) && isExcludedReason(operator.driverStatusForRemoving.value.code)) {
            if (relationshipCode === 'spouse') {
                error.errorMsg = isGPA ? translator(messages.spouseCanNotBeExcluded) : translator(messages.spouseCanNotBeRemoved);
            } else if (isSecondaryNamedInsured(operator) && !isStateCO) {
                error.errorHelpTitle = translator(messages.whatIsSecondaryNamedInsured);
                error.errorHelpMessageContent = translator(messages.definitionOfSecondaryNamedInsured);
                error.errorMsg = translator(messages.notAValidReasonToRemoveSecondaryInsured);
            } else if (canExcludeDrivers) {
                operator.driverType.value = 'excluded';
                addExcludedAlert(operator.value);
            }

            if (error.errorMsg) {
                window.scrollTo(0, 0);
            }
        } else if (isHouseholdReason(operator.driverStatusForRemoving.value.code)) {
            if (relationshipCode === 'spouse' && operator.value.driverStatusForRemoving !== 'never_licensed') {
                error.errorMsg = translator(messages.spouseCannotBeRemovedIfLicensed);
            } else {
                operator.driverType.value = 'household';
                addHouseholdAlert(operator.value);
            }
        } else if (operator.driverStatusForRemoving.value.code === 'insure_driver' || operator.driverStatusForRemoving.value.code === 'discovered') {
            operator.driverType.value = 'rated';
            addRatedDriverAlert(operator.value);
        }
        return error;
    };

    const handleRemovalValidations = (operator, removedObj) => {
        switch (operatorType || operator.value.driverType) {
            case 'household':
                return vehicleOperatorRemovalRules(operator, removedObj);
            case 'excluded':
                if (!isUMB_NP && isExcludedReason(operator.driverStatusForRemoving.value.code)) {
                    const error = { errorMsg: null, errorHelpTitle: null, errorHelpMessageContent: null };
                    if (operator.value.driverStatusForRemoving === 'unwanted') {
                        error.errorMsg = translator(messages.notAValidReasonToRemoveExclusion);
                    } else if (operator.value.driverStatusForRemoving === 'license_revoked') {
                        error.errorMsg = translator(messages.weRequireThisDriverToBeExcluded);
                    }
                    return error;
                }
                return vehicleOperatorRemovalRules(operator, removedObj);
            default:
                return vehicleOperatorRemovalRules(operator, removedObj);
        }
    };

    const jumpToSelectedStepByStepID = (newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode)
    };

    const saveEndorsement = () => {
        setAddHouseMemberWarning(false);
        clearBlockAlerts(alertNames.vehicleOperatorSaveErrorMessage);
        
        submissionVM.value.portalCurrentStep_ML = currentStep.mlStepName;
        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saving, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');

            const index = updatedEndorsementVM.lobData.personalUmbrella.coverables.vehicleOperators.value.findIndex(
                (drv) => drv.fixedID === selectedVehicleOperator.fixedID.value ||
                    vehicleOperatorsVM.value.findIndex((driverVal) => driverVal.fixedID === drv.fixedID) === -1);
            const selectedDriver = updatedEndorsementVM.lobData.personalUmbrella.coverables.vehicleOperators.getElement(index);

            const details = {
                data: selectedVehicleOperator.value.contact.getDisplayName(' '),
                fixedId: selectedDriver.fixedID.value,
                pageAction: isAddOrEditOrRemove,
                driverType: operatorType
            };
            MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader ,currentStep);

            const updatedVehicleOperatorsVal = _.get(updatedEndorsementVM, `${VEHICLE_OPERATORS_PATH}.value`);
            _.set(vehicleOperatorsVM, 'value', updatedVehicleOperatorsVal);
            const singularDriverTypeDisplayName = getDriverTypeDisplayName(true);
            const pluralDriverTypeDisplayName = getDriverTypeDisplayName(false);
            MLTagManagerService.sendFormStepPolicyChangeStep(`${isAddOrEditOrRemove} ${singularDriverTypeDisplayName}`, pluralDriverTypeDisplayName);
            updateWizardSubmissionData(updatedEndorsementVM);
            jumpToSelectedStepByStepID('endorsementAdditionalChanges');
        }).catch(() => {
            setAddHouseMemberWarning(true);
        });
    };

    const driverTypeForNPUmb = (Operator) => {
        const status = Operator.driverStatusForAdding.value.code
        switch(status) {
            case "own_insurance":
                // _.set(selectedVehicleOperator, "driverType.value.code", 'nondriver');
                Operator.driverType.value = 'nondriver';
                break;
            case "license_revoked":
                Operator.driverType.value = 'excluded';
                break;
            case "abroad":
                Operator.driverType.value = 'outofhousehold';
                break;
            case "mission":
                Operator.driverType.value = 'outofhousehold';
                break;
            case "permit":
                Operator.driverType.value = 'permitted';
                break;
            case "insure_driver":
                Operator.driverType.value = 'rated';
                break;
            case "unwanted":
                Operator.driverType.value = 'excluded';
                break;   
            case "unabletodrive":
                Operator.driverType.value = 'excluded';
                break; 
            case "registeredowner":
                Operator.driverType.value = 'excluded';
                break;
            case "discovered":
                Operator.driverType.value = 'rated';
                break;
            default:
                break;

        }
        return Operator;

    }

    const driverRemovalTypeForNPUmb = (Operator) => {
        const status = Operator.driverStatusForRemoving.value.code
        switch(status) {
            case "unknown":
                Operator.driverType.value = 'nondriver';
                return 'nondriver';
            case "deceased":
                Operator.driverType.value = 'nondriver';
                return 'nondriver';
            case "own_insurance":
                Operator.driverType.value = 'nondriver';
                return 'nondriver';
            case "license_revoked":
                Operator.driverType.value = 'excluded';
                return 'excluded';
            case "abroad":
                Operator.driverType.value = 'outofhousehold';
                return 'outofhousehold';
            case "mission":
                Operator.driverType.value = 'outofhousehold';
                return 'outofhousehold';
            case "permit":
                Operator.driverType.value = 'permitted';
                return 'permitted';   
            case "unwanted":
                Operator.driverType.value = 'excluded';
                return 'excluded'; 
            case "registeredowner":
                Operator.driverType.value = 'excluded';
                return 'excluded';
            case "unabletodrive":
                Operator.driverType.value = 'excluded';
                return 'excluded';
            default:
                return undefined;

        }

    }

    const onNext = () => {
        selectedVehicleOperator.contact.updated.value = true;

        // US985436 -> if child component has error -> block navigation
        if(isComponentErrorPresent){
            return;
        }

        if (!(selectedVehicleOperator.aspects.valid && selectedVehicleOperator.aspects.subtreeValid)) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp + 1);
            return;
        }

        clearBlockAlerts(alertNames.vehicleOperatorErrorMessage);

        if (!isStateND && isAddOrEditOrRemove === 'add' && _.includes(['license_revoked', 'unwanted'], selectedVehicleOperator.value.driverStatusForAdding)) {
            const excludedDriverValidationResult = validateExcludedDriverAddition(selectedVehicleOperator, submissionVM, vehicleOperatorsVM.value);
            if (excludedDriverValidationResult.hasError) {
                addVehicleOperatorErrorAlert(excludedDriverValidationResult.errorMessage);
                return;
            }
        }

        const removedObj = { removedOperator: null };
        let error;
        if (isAddOrEditOrRemove === 'remove') {
            error = handleRemovalValidations(selectedVehicleOperator, removedObj);
        } else {
            error = vehicleOperatorEditRules(selectedVehicleOperator, removedObj);
        }

        if (!error.errorMsg) {
            error.errorHelpTitle = null;
            error.errorHelpMessageContent = null;
            clearBlockAlerts(alertNames.vehicleOperatorErrorMessage);
            const lbl = MLEndorsementUtil.stepLabel(currentStep);
            MLEndorsementUtil.setEndorsementDescription(lbl, submissionVM, translator);

            if (!_.isEmpty(removedObj.removedOperator)) {
                const payloadVM = viewModelService.clone(submissionVM)
                const index = vehicleOperatorsVM.value.findIndex((vo) => vo === selectedVehicleOperator.value);

                payloadVM.lobData.personalUmbrella.coverables.vehicleOperators.value.splice(index, 1);

                const promise = EndorsementService.saveEndorsement([payloadVM.value], authHeader);

                showProgressDialog(messages.saving, promise);
                promise.then((updatedPolicyChange) => {
                    const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
                    const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
                    const updatedVehicleOperatorsVal = _.get(updatedEndorsementVM, `${VEHICLE_OPERATORS_PATH}.value`);

                    _.set(vehicleOperatorsVM, 'value', updatedVehicleOperatorsVal);

                    const details = {
                        data: removedObj.removedOperator.contact.getDisplayName(' '),
                        fixedId: removedObj.removedOperator.fixedID,
                        pageAction: isAddOrEditOrRemove
                    };

                    addRemovedVehicleOperatorToAvailableCandidates(submissionVM, selectedVehicleOperator.value);
                    addRemovalAlert(removedObj.removedOperator);
                    MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader);
                    
                    const singularDriverTypeDisplayName = getDriverTypeDisplayName(true);
                    const pluralDriverTypeDisplayName = getDriverTypeDisplayName(false);
                    
                    clearExceptionErrors()
                    MLTagManagerService.sendFormStepPolicyChangeStep(`${isAddOrEditOrRemove} ${singularDriverTypeDisplayName}`, pluralDriverTypeDisplayName);
                    jumpToSelectedStepByStepID('endorsementAdditionalChanges');
                })
                .catch((e) => {
                    handleExceptionErrors(e)
                })
            } else {
                saveEndorsement();
            }
        } else {
            if (error.errorHelpTitle && error.errorHelpMessageContent && isAMP) {
                addErrorAlert(error.errorMsg, alertNames.vehicleOperatorErrorMessage, error.errorHelpTitle, error.errorHelpMessageContent);
            } else {
                addErrorAlert(error.errorMsg, alertNames.vehicleOperatorErrorMessage);
            }
            window.scrollTo(0, 0);
        }
    };

    const resolvers = {
        resolveComponentMap: {
            mlInsuredCandidatesComponent: MLInsuredCandidatesComponent,
            mladdvehicleoperatorcomponent: MLAddVehicleOperatorComponent,
            mlvehicleoperatorstatuscomponent: MLVehicleOperatorStatusComponent,
            mldriverchangedisclosurecomponent: MLDriverChangeDisclosureComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        headerTitleAdd: {
            visible: isAddOrEditOrRemove === 'add',
            content: translator(messages.addX, { driverType: getDriverTypeDisplayName(true).toLowerCase() })
        },
        headerMsgAdd: {
            visible: isAddOrEditOrRemove === 'add' && selectedVehicleOperator.driverType.value.code === 'rated',
            content: translator(messages.addDetailsForThisDriver)
        },
        headerTitleEdit: {
            visible: isAddOrEditOrRemove === 'edit',
            content: translator(messages.editX, { driverType: getDriverTypeDisplayName(true).toLowerCase() })
        },
        headerMsgEdit: {
            visible: isAddOrEditOrRemove === 'edit' && selectedVehicleOperator.driverType.value.code === 'rated',
            content: translator(messages.updateDetailsForThisDriver)
        },
        headerTitleRemove: {
            visible: isAddOrEditOrRemove === 'remove',
            content: getRemoveLabel()
        },
        addEditContainer: {
            visible: isAddOrEditOrRemove !== 'remove'
        },
        insuredCandidatesComponent: {
            visible: isAddOrEditOrRemove === 'add' && !isBackwardsNavigation,
            submissionVM,
            vehicleOperatorVM: selectedVehicleOperator,
            driverType: selectedVehicleOperator.driverType.value.code,
            setInsuredCandidateSelected,
            onValueChange: refreshData,
            viewModelService
        },
        addEditComponent: {
            visible: insuredCandidateSelected || isAddOrEditOrRemove !== 'add' || (isAddOrEditOrRemove === 'add' && selectedVehicleOperator.driverType.value.code === 'rated'),
            vehicleOperator: selectedVehicleOperator,
            driverType: selectedVehicleOperator.driverType.value.code,
            canExcludeDrivers,
            onValidate,
            showValidationErrors,
            vehicleOperatorsVM,
            checkUniqueSelf,
            checkUniqueSpouse,
            submissionVM
        },
        disclosureContainer: {
            visible: (isAddOrEditOrRemove === 'add' || isAddOrEditOrRemove === 'edit' ) && selectedVehicleOperator.driverType.value.code === 'rated'
        },
        disclosureComponent: {
            submissionVM,
            driver: selectedVehicleOperator,
            isPUP: true,
            refreshData,
            onValidate,
            showValidationErrors
        },
        removeContainer: {
            visible: isAddOrEditOrRemove === 'remove'
        },
        removeComponent: {
            vehicleOperator: selectedVehicleOperator,
            label: translator(messages.reasonForRemovingX, { name: getFullNameFormatted(selectedVehicleOperator.contact.value) }),
            driverType: selectedVehicleOperator.driverType.value.code,
            canExcludeDrivers,
            isRemove: true,
            isPolicyChange: true,
            onValidate,
            showValidationErrors,
            setIsComponentErrorPresent
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isAMP ? '' : messages.makeChanges}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );

};

VehicleOperatorPagePolChange.propTypes = wizardProps;
export default VehicleOperatorPagePolChange;